import { Component, Input, EventEmitter, Output, ViewChild, ElementRef, OnDestroy, OnInit, OnChanges, SimpleChanges, AfterViewInit, SecurityContext } from '@angular/core';
import * as moment from 'moment';
import { FormArray, FormGroup } from '@angular/forms';
import { ErrorTooltipComponent } from '../lightshow-preview/Components/error-tooltip.component';
import { EditorMode, SegmentMode } from '../lightshow-preview/lightshow-state';
import { LightShowService } from '../_services/light-show.service';
import { LightShowOperation } from '../lightshow-preview/Operations/lightshow-operation';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as WaveSurfer from 'wavesurfer.js';
import { DomSanitizer } from '@angular/platform-browser';
import { Engine } from '../_models/Engine';
import { restrictImageSegmentStartTime } from '../_shared/utils';

@Component({
  selector: 'app-mp3-player',
  templateUrl: './mp3-player.component.html',
  styleUrls: ['./mp3-player.component.css']
})
export class Mp3PlayerComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input()
  public timeFactorMs = 1;
  @Input()
  public set url(url: any) {
    const sanitizedUrl = this.sanitizer.sanitize(SecurityContext.URL, url);

    this._url = url;
    this._sanitizedUrl = sanitizedUrl;

    if (url && this.wavesurfer) {
      this.waveformLoading = true;

      this.wavesurfer.destroy();
      this.wavesurfer = null;
      this._initializeWaveform();
      this.wavesurfer.load(sanitizedUrl);

      this.wavesurfer.once('ready', () => {
        this.waveformLoading = false;
        const event = new Event('mousemove');
        document.dispatchEvent(event);
      });
    }
  }
  @Input()
  public segmentsFormArray: FormArray = new FormArray([]);
  @Input()
  public selectedSegment: FormGroup;
  @Input()
  public cropEndPercent = 0;
  @Input()
  public canCropEnd = false;
  @Input()
  public set selectedSegmentIndex(value: number) {
    this._selectedSegmentIndex = value;
    this.selectedSegment = this.segmentsFormArray.controls[value] as FormGroup;
  }
  @Input()
  currentSoundEffect: HTMLAudioElement;
  @Input()
  public timelineError: ErrorTooltipComponent;
  @Input()
  editorMode: EditorMode;
  @Input()
  segmentMode: SegmentMode;
  @Input()
  torchArray: any[];
  @Input()
  main: HTMLDivElement;
  @Input()
  useForLightshowEditor: boolean;
  @Input()
  fullScreenPreviewMode = false;
  @Input()
  disableHoverTips = false;
  @Input()
  public currentUserEngine: Engine = null;
  @Input()
  public venueId = 0;

  @Output()
  segmentSelected: EventEmitter<SegmentSelectedData> = new EventEmitter<SegmentSelectedData>();
  @Output()
  playbackPaused: EventEmitter<any> = new EventEmitter<any>();
  @Output()
  playbackStopped: EventEmitter<any> = new EventEmitter<any>();
  @Output()
  playbackStarted: EventEmitter<any> = new EventEmitter<any>();
  @Output()
  playPositionChanged: Subject<number> = new Subject<number>();
  @Output()
  durationChanged: EventEmitter<number> = new EventEmitter<number>();
  @Output()
  cropEndPercentChanged: EventEmitter<number> = new EventEmitter<number>();
  @Output()
  updateFades = new EventEmitter<void>();
  @Output()
  segmentDropped = new EventEmitter<any>();

  @ViewChild('audio', {static: false})
  audioRef: ElementRef;
  @ViewChild('groove', {static: false})
  grooveRef: ElementRef;
  @ViewChild('seekThumb', {static: false})
  seekThumbRef: ElementRef;
  @ViewChild('playerRoot', {static: false})
  playerRootRef: ElementRef;
  @ViewChild('cropEndGrip', {static: false})
  cropEndGripRef: ElementRef;
  @ViewChild('volume', {static: false})
  volume: ElementRef;
  @ViewChild('grooveContainer', {static: false})
  grooveContainerRef: ElementRef;

  public zoomFocus = false;

  public segmentModeType = SegmentMode;

  public get url() {
    return this._url;
  }

  public get selectedSegmentIndex() {
    return this._selectedSegmentIndex;
  }

  private shoudSyncWithSlider = true;
  private playTimer: any;
  private documentMouseMoveListener: any;
  private wavesurfer: any;
  private _url: string;
  private _selectedSegmentIndex: number;
  private _ngDestroy$ = new Subject<void>();
  private _sanitizedUrl: string;

  public isPlaying = false;
  public playPosition = 0;
  public totalDuration = 0;
  public croppedDuration = 0;
  public playbackDone = 0;
  public slidermousedown: any;
  public slidermouseup: any;
  public slidermousemove: any;
  public groovemouseup: () => void;
  public selectedOperation: LightShowOperation;
  public throttledMouseMove;
  public mainKeyDown: (e: KeyboardEvent) => void;
  public mainKeyUp: (e: KeyboardEvent) => void;
  public documentMouseUp: () => void;

  public performanceMs: number;
  public zoomScale = 1;
  public zoomScaleDisplay = 100;
  public isDraggingSliderBody: boolean;
  public waveformLoading = true;
  public readonly SEEK_THUMB_DRAGGABLE_HALF_AREA = 2; // this represents number of pixels
  private shouldFreeze: boolean;

  private get MINIMUM_TORCH_DURATION() {
    return this.lightshowService.MINIMUM_TORCH_DURATION;
  }

  constructor(private lightshowService: LightShowService,
    private sanitizer: DomSanitizer) { }

  ngOnInit() {
    this.lightshowService.torchFreezer$
      .pipe(takeUntil(this._ngDestroy$))
      .subscribe(bool => {
        this.shouldFreeze = bool;
      });

    this.segmentsFormArray.valueChanges
      .pipe(takeUntil(this._ngDestroy$))
      .subscribe(() => {
        if (this.selectedSegment == null) {
          this.selectedSegment = <FormGroup>this.segmentsFormArray.controls.find(() => true);
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['url'] && changes['url'].currentValue) {
      this.stop();
    }
  }

  ngAfterViewInit() {
    this._attachEventListeners();
    this._initializeWaveform();

    if (this._sanitizedUrl && this.wavesurfer) {
      this.waveformLoading = true;

      this.wavesurfer.destroy();
      this.wavesurfer = null;
      this._initializeWaveform();
      this.wavesurfer.load(this._sanitizedUrl);

      this.wavesurfer.once('ready', () => {
        this.waveformLoading = false;
        const event = new Event('mousemove');
        document.dispatchEvent(event);
      });
    }
  }

  public seekPosition(timeInSeconds: number) {
    const audio = this.audioRef.nativeElement;
    audio.currentTime = timeInSeconds;
    this.playPositionChanged.next(timeInSeconds * 1000);
  }

  public selectSegment(seg: FormGroup, index: number) {
    this.selectedSegment = seg;
    this.segmentSelected.emit({ segment: seg, index: index });
  }

  selectOperation(uniqueId: string) {
    this.lightshowService.selectOperation(uniqueId);

    if (!this.lightshowService.isCtrlPressed) {
      this.lightshowService.operationSelected$.next(uniqueId);
    }
  }

  deselectOperation(uniqueId: string) {
    this.lightshowService.deselectOperation(uniqueId);
  }

  checkIfOperationIsSelected(uniqueId: string) {
    return this.lightshowService.checkIfOperationIsSelected(uniqueId);
  }

  public play() {
    this.isPlaying = true;
    const audioPromise = this.audioRef.nativeElement.play();
    audioPromise.then().catch(_ => _);
    this.playbackStarted.emit();
    // Run tick loop while playing. We cannot rely on audio object to advance the position since it fires every 300ms or so, but we need a 20ms resolution.
    // When the audio object ticks, though, we do need to sync with it since the user can change the current position by dragging the seek thumb.
    let playStartTime: moment.Moment;
    let playInitialPosition: number;
    this.playTimer = setInterval(() => {
      if (this.shoudSyncWithSlider) {
        playStartTime = moment();
        playInitialPosition = this.audioRef.nativeElement.currentTime * 1000;
        this.shoudSyncWithSlider = false;
      }
      if (this.playPosition >= this.totalDuration - (this.cropEndPercent / 100 * this.totalDuration)) {
        this.stop();
      } else {
        this.playPositionChanged.next(playInitialPosition + moment.duration(moment().diff(playStartTime)).asMilliseconds());
      }
    }, 40); // This was 20ms, since the smallest segment is 100ms I think everything below that should be okay

    if (this.lightshowService.selectedOperations) {
      this.lightshowService.selectedOperations = [];
    }
    this.lightshowService.selectedOperationChange$.next();
  }

  public formatSecondsToMinutes(time: number) {
    return moment(time).format('mm:ss');
  }

  public pause() {
    clearInterval(this.playTimer);
    this.isPlaying = false;
    this.audioRef.nativeElement.pause();
    this.playbackPaused.emit();
  }

  public stop() {
    clearInterval(this.playTimer);
    this.isPlaying = false;
    if (this.audioRef) {
      this.audioRef.nativeElement.pause();
      this.audioRef.nativeElement.currentTime = 0;
    }
    this.playPosition = 0;
    this.playbackStopped.emit();
    this.playPositionChanged.next(this.playPosition);
  }

  public deleteSegment(segmentIndex: number) {
    this.segmentsFormArray.removeAt(segmentIndex);
    if (this.segmentsFormArray.length) {
      if (segmentIndex === this.selectedSegmentIndex) {
        const controls = this.segmentsFormArray.controls.filter(control => !control.value.hidden);
        this.selectSegment(<FormGroup>controls[0], this.segmentsFormArray.controls.indexOf(controls[0]));
      } else if (segmentIndex < this.selectedSegmentIndex) {
        this.selectSegment(<FormGroup>this.segmentsFormArray[this.selectedSegmentIndex - 1], this.selectedSegmentIndex - 1);
      }
    } else {
      this.selectSegment(null, null);
    }
  }

  public hideSegment(segmentIndex: number){
    if (this.segmentsFormArray.length){
      this.segmentsFormArray.value[segmentIndex].hidden = !this.segmentsFormArray.value[segmentIndex].hidden;
    }
  }

  public trackByIndex(index) {
    // performance optimization - reuse the same UI when ever possible
    // this is important in the case when moving the cropEnd anchor forces recalculation of segments
    return index;
  }

  getSegmentFormGroup(index: number): FormGroup {
    return this.segmentsFormArray.controls[index] as FormGroup;
  }

  onSegmentDrop(event) {

    const groove = this.grooveRef?.nativeElement;

    if (!groove || !event) {

      return;
    }

    const offsetX = event.event.clientX - groove.getBoundingClientRect().x;

    this.segmentDropped.emit({ 
      ...event, 
      offsetX, 
      grooveWidth: groove.clientWidth, 
      zoomScale: this.zoomScale, 
      croppedDuration: this.croppedDuration,
      duration: this.totalDuration 
    });
  }

  private _initializeWaveform() {
    this.wavesurfer = WaveSurfer.create({
      container: '#waveform',
      waveColor: 'lightgrey',
      progressColor: 'purple',
      barWidth: 1,
      forceDecode: true,
      interact: false,
      normalize: true,
      hideScrollbar: true,
      partialRender: true,
      pixelRatio: 10,
      responsive: true,
      barGap: .5
    });

    this.wavesurfer.once('error', (err) => {
      console.error(err);
      this.waveformLoading = false;
      const event = new Event('mousemove');
      document.dispatchEvent(event);
    });
  }

  private _attachEventListeners() {
    let isDraggingSeekThumb: boolean;
    let isDraggingCropEndGrip: boolean;
    let startPercentage: number;
    let startXCursor: number;

    const self = this;
    const audio = this.audioRef.nativeElement;
    const seekThumb = this.seekThumbRef.nativeElement;
    const groove = this.grooveRef.nativeElement;
    const cropEndGrip = this.cropEndGripRef.nativeElement;

    this.cropEndPercent = this.cropEndPercent || 0;

    // when audio is loaded, read mp3 duration, and adjust position
    audio.ondurationchange = function () {
      if (Math.round(audio.duration * 10000) !== Math.round(self.totalDuration * 10)) {
        self.stop();
        self.totalDuration = audio.duration * 1000;
        self.croppedDuration = self.totalDuration - (self.cropEndPercent / 100 * self.totalDuration);
        self.durationChanged.emit(self.totalDuration);
      }
    };

    // when audio play ends, set isPlaying to false
    audio.onended = function () {
      self.stop();
    };

    // when audio playback progresses, move the seek thumb
    audio.ontimeupdate = function () {
      self.shoudSyncWithSlider = true;
    };

    this.playPositionChanged
      .pipe(takeUntil(this._ngDestroy$))
      .subscribe(pos => {
        self.playbackDone = pos / this.totalDuration * 100;
        self.playPosition = pos;
      });

    // when seek tab is dragged, move the seek tab and audio playback position
    seekThumb.onmousedown = e => {
      if (e.which === 1 && !this.shouldFreeze) {
        startXCursor = e.clientX;
        startPercentage = this.playbackDone;
        isDraggingSeekThumb = true;
      }
    };

    cropEndGrip.onmousedown = e => {
      if (e.which === 1) {
        startXCursor = e.clientX;
        startPercentage = this.cropEndPercent;
        isDraggingCropEndGrip = true;
        e.cancelBubble = true;
      }
    };

    // when mouseup, stop dragging
    document.addEventListener('mouseup', this.documentMouseUp = () => {
      if (isDraggingCropEndGrip && this.segmentsFormArray) {
        const count = this.segmentsFormArray.value.filter(seg => !seg.hidden).length;

        this.segmentsFormArray.controls.forEach(control => {
          control.patchValue({
            hidden: control.value.position + control.value.duration > this.croppedDuration,
          });
        });

        const filterByHiddenStatus = this.segmentsFormArray.controls.filter(segGroup => !segGroup.value.hidden);

        // this conditional statement enesures that the seekthumb can not be positioned after the cropEnd
        if (self.playbackDone > 100 - self.cropEndPercent && audio.currentTime > self.croppedDuration / 1000) {
          self.playbackDone = 0;
          audio.currentTime = 0;
          self.playPositionChanged.next(0);
        }

        if (filterByHiddenStatus.length < count) {
          const lastNonHiddenElement = filterByHiddenStatus[filterByHiddenStatus.length - 1];
          this.selectSegment(lastNonHiddenElement as FormGroup, this.segmentsFormArray.controls.indexOf(lastNonHiddenElement));
        }
      }
      isDraggingSeekThumb = isDraggingCropEndGrip = false;
    });

    this.documentMouseMoveListener = e => {
      if (!this.waveformLoading) {
        const pctDiff = 100 * (e.clientX - startXCursor) / groove.clientWidth;

        if (isDraggingSeekThumb) {
          self.playbackDone = Math.max(0, Math.min(100 - this.cropEndPercent, Math.min(100, startPercentage + pctDiff / this.zoomScale)));
          const currentTime = (this.playbackDone * audio.duration) / (100);
          audio.currentTime = !Number.isFinite(currentTime) ? 0 : currentTime;
          self.playPositionChanged.next(audio.currentTime * 1000);
          e.preventDefault();
        } else if (isDraggingCropEndGrip) {
          self.cropEndPercent = Math.max(0, Math.min(100, startPercentage - pctDiff / this.zoomScale));
          self.croppedDuration = self.totalDuration - (self.cropEndPercent / 100 * self.totalDuration);

          self.cropEndPercentChanged.emit(self.cropEndPercent);
          e.preventDefault();
        }
      }
    };


    // when mouse is moved, if dragging, move the dragged element and run post-move function.
    document.addEventListener('mousemove', this.documentMouseMoveListener);

    // MP3 SLIDER EVENT LISTENER
    let startPosition: number;
    let startDuration: number;
    let isDraggingSlider = false;
    let isDraggingLeftGrip = false;
    let isDraggingRightGrip = false;
    let originalOperationValues = [];
    let targetOperation: HTMLElement;
    let centerForShiftSelection: LightShowOperation;
    let sliderHasMoved = false;
    let selectedOperation;
    let markAsDeselectable = false;

    this.main.addEventListener('keydown', this.mainKeyDown = (e) => {
      const eventTargetElement = e.target as HTMLElement;

      // Prevent main from scrolling the page when pressing space(play/pause)
      if (eventTargetElement.tagName === 'DIV' && eventTargetElement.className === 'main') {
        e.preventDefault();
      }

      if (!e.repeat) {
        if (e.ctrlKey && (e.key === 'a' || e.key === 'A')) {
          e.preventDefault();
          this.lightshowService.controlPlusAPressed$.next();
        } else if (e.key === 'Delete') {
          this.lightshowService.deleteKeyPressed$.next();
        } else if (e.key === 'Control') {
          this.lightshowService.isCtrlPressed = true;
        } else if (e.key === 'Shift') {
          this.lightshowService.isShiftPressed = true;
        }
      }
    });

    this.main.addEventListener('keyup', this.mainKeyUp = (e) => {
      const eventTargetElement = e.target as HTMLElement;

      if (!e.repeat) {
        if (e.key === 'Control') { // TODO: Test on mac command key
          this.lightshowService.isCtrlPressed = false;
        } else if (e.key === 'Shift') {
          centerForShiftSelection = undefined;
          this.lightshowService.isShiftPressed = false;
        }

        if (e.code === 'Space' && eventTargetElement.tagName !== 'INPUT') {
          if (this.editorMode === EditorMode.SCREEN ) {
            this.isPlaying ? this.pause() : this.play();
          } else if (!this.isPlaying) {
            this.play();
          }
        }
      }
    });

    this.main.addEventListener('focusout', (e) => {
      this.lightshowService.isCtrlPressed = false;
      this.lightshowService.isShiftPressed = false;
    });

    let screenStartPosition: number;
    let screenStartDuration: number;
    let screenStartXCursor: number;
    let screenIsDraggingSlider = false;
    let screenIsDraggingLeftGrip = false;
    let screenIsDraggingRightGrip = false;
    let screenOriginalPosition = 0;
    let screenOriginalDuration = 0;
    let targetId: number;

    groove.addEventListener('mousedown', this.slidermousedown = (e) => {
      groove.focus();

      if (!this.waveformLoading) {
        if (this.segmentsFormArray && this.segmentsFormArray.value.length && e.which === 1 && e.target.className.includes('screen')) {
          if (e.target.className.includes('grip')) {
            targetId = +e.target.parentElement.id;
          } else {
            targetId = +e.target.id;
          }

          screenStartXCursor = e.clientX;
          screenStartPosition = +self.getSegmentFormGroup(targetId).value.position;
          screenStartDuration = +self.getSegmentFormGroup(targetId).value.duration;
          screenIsDraggingLeftGrip = e.target.className.includes('screen grip grip-left');
          screenIsDraggingRightGrip = e.target.className.includes('screen grip grip-right');
          screenIsDraggingSlider = this.isDraggingSliderBody = !screenIsDraggingLeftGrip && !screenIsDraggingRightGrip;

          screenOriginalPosition = self.getSegmentFormGroup(targetId).value.position;
          screenOriginalDuration = self.getSegmentFormGroup(targetId).value.duration;

          if (this.selectedSegment && (self.getSegmentFormGroup(targetId).value.uniqueid !== this.selectedSegment.value.uniqueid)) {
            this.selectSegment(self.getSegmentFormGroup(targetId), targetId);
          }
        }

        if (this.torchArray && this.torchArray.length > 0 && e.which === 1 && !e.target.className.includes('screen') && !this.shouldFreeze) {
          targetOperation = e.target;
          sliderHasMoved = false;
          let id: number;

          if (targetOperation.className.includes('grip')) {
            targetOperation = targetOperation.parentElement;
            id = +targetOperation.id;
          } else {
            id = +e.target.id;
          }

          const operation = this.torchArray[id];
          selectedOperation = operation;

          markAsDeselectable = this.checkIfOperationIsSelected(operation.uniqueId) ? true : false;

          startXCursor = e.clientX;
          startPosition = +operation.position;
          startDuration = +operation.duration;
          isDraggingLeftGrip = e.target.className.includes('grip grip-left');
          isDraggingRightGrip = e.target.className.includes('grip grip-right');
          isDraggingSlider = this.isDraggingSliderBody = e.target.className.includes('slider-body');

          // this will make sure that we only select operation if we're clicked on one!
          if (e.target.className.includes('slider-body') || e.target.className.includes('grip')) {
            if (this.lightshowService.isShiftPressed && this.lightshowService.selectedOperations.length > 0) {
              const sortedCopy = self.torchArray.sort((a, b) => a - b);
              centerForShiftSelection = centerForShiftSelection
                ? centerForShiftSelection
                : sortedCopy.find(op => op.uniqueId === this.lightshowService.selectedOperations[this.lightshowService.selectedOperations.length - 1]);

              this.selectOperation(operation.uniqueId);

              const leftEdgeOfInterval = Math.min(centerForShiftSelection.position, operation.position);
              const rightEdgeOfInterval = Math.max(centerForShiftSelection.position, operation.position);

              sortedCopy.forEach(op => {
                if (op.position >= leftEdgeOfInterval && op.position <= rightEdgeOfInterval) {
                  this.selectOperation(op.uniqueId);
                } else if (!this.lightshowService.isCtrlPressed) {
                  this.deselectOperation(op.uniqueId);
                }
              });

            } else {
              this.selectOperation(operation.uniqueId);
            }
          }

          // create a copy of the torch array
          this.torchArray.forEach(op => {
              originalOperationValues.push({ uniqueId: op.uniqueId, position: op.position, duration: op.duration });
          });

          if (this.shouldFreeze) {
            isDraggingLeftGrip = false;
            isDraggingRightGrip = false;
            isDraggingSlider = false;
          }

          // to prevent the seek thumb from moving. we don't want to preventDefault() though, because we want this element to be focusable
          e.stopPropagation();
        }

        if (e.which === 1 && !e.target.className.includes('slider-body') && !e.target.className.includes('grip')) {
          isDraggingSeekThumb = !this.shouldFreeze;
          const offsetX = e.clientX - groove.getBoundingClientRect().x;
          self.playbackDone = Math.min(100 - this.cropEndPercent, (offsetX / groove.clientWidth * 100) / this.zoomScale);
          audio.currentTime = Math.min(this.croppedDuration / 1000, (offsetX / groove.clientWidth * self.totalDuration / 1000) / this.zoomScale);
          self.playPositionChanged.next(audio.currentTime * 1000);
          startXCursor = e.clientX;
          startPercentage = self.playbackDone;
        }

        e.preventDefault();
      }
    });

    window.addEventListener('mouseup', this.slidermouseup = e => {
      if (this.torchArray && this.torchArray.length && (isDraggingSlider || isDraggingLeftGrip || isDraggingRightGrip)) {
        for (let index = 0; index < this.torchArray.length; index++) {
          const currentTorch = this.torchArray[index];

          if (+currentTorch.position < 0) {
            this.torchArray[index].position = 0;
          }

          if (+currentTorch.position + +currentTorch.duration > +self.totalDuration) {
            this.torchArray[index].duration = +self.totalDuration - +currentTorch.position;
          }

          const torchCollide = this.torchArray.find(
            op =>
            // Don't do an overlap check if both torches have ignoreOverlap = true
            !(op.ignoreOverlap && currentTorch.ignoreOverlap) &&
            // Don't compare the same torches against themselves
            op.uniqueId !== currentTorch.uniqueId &&
            // Allow torches with different types to overlap, prevent torches with none or matching sections from overlapping
            (+this.venueId === 0 || !op.sectionList || !currentTorch.sectionList || op.sectionList.filter(s => currentTorch.sectionList.indexOf(s) >= 0).length > 0 || op.flashType === currentTorch.flashType) && (
            // Case #1 - Current Torch overlaps with right side of torch
            (+currentTorch.position < (+op.position + +op.duration) && (+currentTorch.position > +op.position)) ||
            // Case #2 - Current Torch overlaps with left side of torch
            (((+currentTorch.position + +currentTorch.duration) < (+op.position + +op.duration)) && ((+currentTorch.position + +currentTorch.duration) > +op.position)) ||
            // Case #3 - Current Torch completely overlaps another torch
            ((+currentTorch.position < +op.position) && ((+currentTorch.position + +currentTorch.duration) > (+op.position + +op.duration))) ||
            // Case #4 - Current Torch is completely within another torch
            ((+currentTorch.position > +op.position) && (+currentTorch.position + +currentTorch.duration) < (+op.position + +op.duration))
          ));

          if (torchCollide) {
            // torch collide is always right of the selected torch
            const collideSelected =  this.checkIfOperationIsSelected(torchCollide.uniqueId);
            const collideIndex = this.torchArray.indexOf(torchCollide);
            const neighbors = collideIndex - index === 1;

            // const selectedIndex = this.checkIfOperationIsSelected(torchCollide.uniqueId) ? this.torchArray.indexOf(torchCollide) : index;

            // right side collision with neighbor
            if (collideSelected && neighbors) {

              // state of selected torch before moving it
              const savedCollide = originalOperationValues.find(c => c.uniqueId === torchCollide.uniqueId);
              const delta = savedCollide.duration - torchCollide.duration;

              // Snap position of selected torch to edge of current torch we're iterating thru
              this.torchArray[collideIndex].position = currentTorch.position + currentTorch.duration;

              if (torchCollide.flashType !== currentTorch.flashType && torchCollide.flashType === 0) {

                this.torchArray[collideIndex].position += 50;
              }

              // If the duration didn't change
              if (delta === 0) {

                // Make sure duration stays unchanged
                this.torchArray[collideIndex].duration = torchCollide.duration + torchCollide.position - this.torchArray[collideIndex].position;

              } else {

                // Make sure the torch ends where it did before anything was moved
                this.torchArray[collideIndex].duration = savedCollide.duration + savedCollide.position - this.torchArray[collideIndex].position;
              }

            // left side collision with neighbor
            } else if (neighbors) {

              const savedCurrent = originalOperationValues.find(c => c.uniqueId === currentTorch.uniqueId);

              // Keep track of whether position or duration of current torch needs to change if torchCollide is a solid torch
              let necessarySolidTorchSpacePos = 0;
              let necessarySolidTorchSpaceDur = 0;

              if(savedCurrent) {

                const delta = savedCurrent.duration - currentTorch.duration;

                // Current torch position is changing, duration must stay the same
                if (delta === 0) {

                  this.torchArray[index].duration = savedCurrent.duration;

                  // If torchCollide is solid and currentTorch is different flash type
                  // We need to substract 50ms from current torch position
                  if (torchCollide.flashType !== currentTorch.flashType && torchCollide.flashType === 0) {

                    necessarySolidTorchSpacePos = 50;
                  }

                }
                // Current torch duration is changing, position must stay the same 
                else {
                  
                  this.torchArray[index].duration = torchCollide.position - savedCurrent.position;

                  // If torchCollid is solid and currentTorch is different flash type
                  // We need to subtract 50 from current torch duration
                  if (torchCollide.flashType !== currentTorch.flashType && torchCollide.flashType === 0) {
                    
                    necessarySolidTorchSpaceDur = 50;
                  }
                }
              }
          
              this.torchArray[index].position = torchCollide.position - this.torchArray[index].duration - necessarySolidTorchSpacePos;
              this.torchArray[index].duration -= necessarySolidTorchSpaceDur;

            } else {
              // return the selected torch to it's original place and display and error
              const savedData = originalOperationValues.find(s => s.uniqueId === currentTorch.uniqueId);
              this.torchArray[index].position = savedData.position;
              this.torchArray[index].duration = savedData.duration;
              this.timelineError.showAndHide('Torches can\'t overlap.');
            }

            isDraggingSlider = this.isDraggingSliderBody = isDraggingLeftGrip = isDraggingRightGrip = false;

          }
        }
      }

      const targetSegmentFG = self.getSegmentFormGroup(targetId);

      if (self.segmentsFormArray.value.length && (screenIsDraggingSlider || screenIsDraggingLeftGrip || screenIsDraggingRightGrip)) {
        for (let index = 0; index < self.segmentsFormArray.value.length; index++) {
          if (self.segmentsFormArray.value.indexOf(targetSegmentFG.value) !== index) {
            const selectedSegmentStart = +targetSegmentFG.value.position;
            const selectedSegmentEnd = selectedSegmentStart + +targetSegmentFG.value.duration;
            const selectedSegmentType = targetSegmentFG.value.segmentType === -1 ? 'unknown' : targetSegmentFG.value.audioUrl ? 'audio' : targetSegmentFG.value.colors.length ? 'color' : 'image';



          if (selectedSegmentType === 'image' && restrictImageSegmentStartTime(this.selectedSegment.value, this.currentUserEngine)) {
            self.getSegmentFormGroup(targetId).patchValue({ position: screenOriginalPosition, duration: screenOriginalDuration });
            screenIsDraggingSlider = screenIsDraggingLeftGrip = screenIsDraggingRightGrip = false;

            this.timelineError.showAndHide(`Image Segments cannot be placed within the first ${this.currentUserEngine.imageSegmentStartTimeThreshold} seconds of a light show`);
            break;
          }

          if (self.segmentsFormArray.value.indexOf(self.getSegmentFormGroup(targetId).value) !== index) {


            const currentSegmentStart = self.segmentsFormArray.value[index].position;
            const currentElementEnd = self.segmentsFormArray.value[index].duration + currentSegmentStart;
            const currentSegmentType = self.segmentsFormArray.value[index].segmentType === -1 ? 'unknown' : self.segmentsFormArray.value[index].audioUrl ? 'audio' : self.segmentsFormArray.value[index].colors.length ? 'color' : 'image';

            if (
              (selectedSegmentType === currentSegmentType) && // First check this, then all other check
              ((selectedSegmentStart > currentSegmentStart && selectedSegmentStart < currentElementEnd)
                || (selectedSegmentEnd > currentSegmentStart && selectedSegmentEnd < currentElementEnd)
                || (selectedSegmentStart < currentSegmentStart && selectedSegmentEnd > currentElementEnd)
                || (selectedSegmentStart === currentSegmentStart && selectedSegmentEnd === currentElementEnd))
            ) {

              targetSegmentFG.patchValue({ position: screenOriginalPosition, duration: screenOriginalDuration });
              screenIsDraggingSlider = this.isDraggingSliderBody = screenIsDraggingLeftGrip = screenIsDraggingRightGrip = false;

              this.timelineError.showAndHide('Segments of the same type can\'t overlap');
              break;
            }
          }
        }
      }
      }

      if (this.lightshowService.isCtrlPressed && !sliderHasMoved && markAsDeselectable) {
        this.lightshowService.deselectOperation(selectedOperation.uniqueId);
      }

      originalOperationValues = [];

      screenIsDraggingSlider = this.isDraggingSliderBody = screenIsDraggingLeftGrip = screenIsDraggingRightGrip = false;
      isDraggingSlider = this.isDraggingSliderBody = isDraggingLeftGrip = isDraggingRightGrip = false;

      if (this.torchArray && this.torchArray.length > 0) {
        this.torchArray.sort((a, b) => a.position - b.position);
      }
    });

    // onmousemove: if dragging, move the dragged element.
    this.slidermousemove = (e: MouseEvent) => {
      // mouse in on operation segment and not on left or right grip, not on groove
      if (this.torchArray && 
          this.torchArray.length && 
          (isDraggingSlider || isDraggingLeftGrip || isDraggingRightGrip) &&
          targetOperation && targetOperation?.parentElement) {

        sliderHasMoved = true;

        const id = +targetOperation.id;

        const operation = this.torchArray[id];

        const slider = targetOperation.parentElement;

        const durationScaled = self.totalDuration / self.timeFactorMs;

        const timeDiff = ((e.clientX - startXCursor) / slider.parentElement.clientWidth) * durationScaled;

        const trackDurationMs = this.totalDuration;

        const cutOff = trackDurationMs - (this.cropEndPercent / 100) * trackDurationMs;

        const cutOffDurationScaled = cutOff / self.timeFactorMs;

        if (timeDiff) {
          if (isDraggingSlider) {
            const oldPos = operation.position;
            const newPos = Math.min(cutOffDurationScaled - operation.duration, Math.max(0, startPosition + timeDiff / this.zoomScale));

            const relativeDifference = newPos - oldPos;

            operation.position = newPos;

            this.torchArray.forEach(op => {
              if (this.checkIfOperationIsSelected(op.uniqueId) && operation.uniqueId !== op.uniqueId) {
                op.position += relativeDifference;
              }
            });

            // operation.position = startPosition + timeDiff < 0 || startPosition + timeDiff >= this.totalDuration - operation.duration ?  operation.position : startPosition + timeDiff;
            // Replacing the above line with the below commented out line would prevent segments from being draggable to the cropped area.
            // Commenting this out because we would also need to push semgents back while dragging the crop area to be consistent about not
            // letting segments under the cropped area. I see no harm in letting segments be under the cropped area so I'm leaving this as is.
            // self.segment.position = Math.min(self.availableDuration - self.segment.duration, Math.max(0, startPosition + timeDiff));
          } else if (isDraggingLeftGrip) {
            operation.position = Math.max(0, Math.min(startPosition + startDuration - 1, startPosition + timeDiff / this.zoomScale));
            operation.duration = Math.max(this.MINIMUM_TORCH_DURATION || 1, Math.min(startPosition + startDuration, startDuration - timeDiff / this.zoomScale));
          } else if (isDraggingRightGrip) {
            // operation.duration = Math.max(150, Math.min(durationScaled - startPosition, startDuration + timeDiff));
            operation.duration = Math.max(this.MINIMUM_TORCH_DURATION || 1, Math.min(cutOffDurationScaled - startPosition, startDuration + timeDiff / this.zoomScale));
          }
          if (isDraggingSlider || isDraggingLeftGrip || isDraggingRightGrip) {
            e.preventDefault();
          }

          this.lightshowService.operationArrayChange$.next();
        }
      }

      // this should be zero, we need to handle minimum segment times elsewhere
      const SEGMENT_MINIMUM_POSITION = 0;
      const targetSegmentFG = self.getSegmentFormGroup(targetId);

      if (targetSegmentFG && !targetSegmentFG.value.frozen && this.segmentsFormArray.value.length && (screenIsDraggingSlider || screenIsDraggingLeftGrip || screenIsDraggingRightGrip)) {
        const durationScaled = self.totalDuration / self.timeFactorMs;
        const timeDiff = ((e.clientX - screenStartXCursor) / groove.clientWidth) * durationScaled;

        if (timeDiff) {
          if (screenIsDraggingSlider) {
            targetSegmentFG.patchValue({
              position: Math.min(
                this.croppedDuration - targetSegmentFG.value.duration,
                Math.max(SEGMENT_MINIMUM_POSITION, Math.min(
                  durationScaled - +targetSegmentFG.value.duration,
                  Math.max(0, screenStartPosition + timeDiff / this.zoomScale)
                ))
              )
            });

            // Replacing the above line with the below commented out line would prevent segments from being draggable to the cropped area.
            // Commenting this out because we would also need to push semgents back while dragging the crop area to be consistent about not
            // letting segments under the cropped area. I see no harm in letting segments be under the cropped area so I'm leaving this as is.
            // self.segment.position = Math.min(self.availableDuration - self.segment.duration, Math.max(0, startPosition + timeDiff));
          } else if (screenIsDraggingLeftGrip && (!targetSegmentFG.value.audioName || !targetSegmentFG.value.audioUrl)) {
            targetSegmentFG.patchValue({
              position: Math.max(
                0,
                Math.min(screenStartPosition + screenStartDuration - 1, screenStartPosition + timeDiff / this.zoomScale)
              )
            });
            targetSegmentFG.patchValue({
              duration: Math.max(
                +targetSegmentFG.value.minDuration || 1,
                Math.min(screenStartPosition + screenStartDuration, screenStartDuration - timeDiff / this.zoomScale)
              )
            });

            this.updateFades.emit();
          } else if (screenIsDraggingRightGrip && (!targetSegmentFG.value.audioName || !targetSegmentFG.value.audioUrl)) {
            targetSegmentFG.patchValue({
              duration: Math.max(
                +targetSegmentFG.value.minDuration || 1,
                Math.min(durationScaled - screenStartPosition, screenStartDuration + timeDiff / this.zoomScale)
              )
            });

            this.updateFades.emit();
          }
          if (screenIsDraggingSlider || screenIsDraggingLeftGrip || screenIsDraggingRightGrip) {
            e.preventDefault();
          }
        }
      }
    };

    groove.addEventListener('mouseup', this.groovemouseup = () => {
      this.lightshowService.sortSegments.next();
    });

    this.throttledMouseMove = this.lightshowService.throttled(50, this.slidermousemove);

    if (this.main) {
      this.main.addEventListener('mousemove', this.throttledMouseMove);
    } else {
      groove.addEventListener('mousemove', this.throttledMouseMove);
    }

    this._attachZoomEventListeners();
  }

  changeZoomScale (event: any) {

    const MAX_SCALE = 10;

    const value = Math.max((event.target.value / 100), 1);
    const newZoom = Math.max(1, Math.min(MAX_SCALE, value));

    this.setZoomScale(newZoom, 0);
  }

  private setZoomScale (newZoom: number, mousePosition: number) {

    const target = this.grooveRef.nativeElement as HTMLDivElement;
    const container = this.grooveContainerRef.nativeElement as HTMLDivElement;

    const oldScale = this.zoomScale;
    this.zoomScale = newZoom;
    const GCRect = container.getBoundingClientRect() as DOMRect;

    const currentScrollCenterX = container.scrollLeft  + GCRect.width / 2;
    const mousePosRelativeToScrollCenter = mousePosition - GCRect.x - GCRect.width / 2;
    const newScrollCenter = currentScrollCenterX + mousePosRelativeToScrollCenter;

    const p = newScrollCenter / (GCRect.width * oldScale);
    const scaledPos =  p * (GCRect.width * newZoom);

    // fire up zoom and scroll
    target.style.transform = `scaleX(${this.zoomScale})`;
    container.scroll(scaledPos - GCRect.width / 2, 0);

    this.zoomScaleDisplay = this.zoomScale * 100;
  }

  private _attachZoomEventListeners () {

    const target = this.grooveRef.nativeElement as HTMLDivElement;

    const MAX_SCALE = 10;
    const FACTOR = 0.5; // The zoom-speed - 1 = +100% zoom per mouse wheel tick

    let scale = 1;

    target.onwheel = (e) => {
     // const offset = this._getOffset(container);

      e.preventDefault();
      // handle zoom
      let delta = e.deltaY * -1.2;
      delta = Math.max(-1, Math.min(1, delta));

      scale += delta * FACTOR * scale;
      scale = Math.round(Math.max(1, Math.min(MAX_SCALE, scale)));

      this.setZoomScale(scale, e.clientX);
    };
  }

  private _getOffset(elem: HTMLElement) {
    const rect = elem.getBoundingClientRect(),
      scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    return rect.left + scrollLeft;
  }

  ngOnDestroy() {
    document.removeEventListener('mousemove', this.documentMouseMoveListener);
    document.removeEventListener('mouseup', this.documentMouseUp);
    window.removeEventListener('mouseup', this.slidermouseup);
    this.grooveRef.nativeElement.removeEventListener('mouseup', this.groovemouseup);

    if (this.main) {
      this.main.removeEventListener('mousemove', this.throttledMouseMove);
      this.main.addEventListener('keydown', this.mainKeyDown);
    }

    this.grooveRef.nativeElement.removeEventListener('mousemove', this.throttledMouseMove);

    clearInterval(this.playTimer);
    this.audioRef.nativeElement.pause();

    this._ngDestroy$.next();
    this._ngDestroy$.complete();
  }
}

export class Segment {
  frozen?: boolean;
  position: number;
  duration: number;
  minDuration?: number;
  color?: string;
}

export interface SegmentSelectedData {
  segment?: any;
  index: number;
}
